import { Skeleton } from "../ui/skeleton";

/** Prose-style lines for markdown document surfaces (Knowledge, Organization). */
export function MorrowDocumentSkeleton() {
  return (
    <div className="w-full space-y-3 py-2" aria-hidden>
      <Skeleton className="h-3.5 w-1/3 rounded-full" />
      <Skeleton className="h-3 w-full rounded-full" />
      <Skeleton className="h-3 w-11/12 rounded-full" />
      <Skeleton className="h-3 w-4/5 rounded-full" />
      <Skeleton className="mt-5 h-3.5 w-1/4 rounded-full" />
      <Skeleton className="h-3 w-3/4 rounded-full" />
      <Skeleton className="h-3 w-2/3 rounded-full" />
    </div>
  );
}

/** Person-card grid used on the Team page while members load. */
export function MorrowCardGridSkeleton({ count = 8 }: { readonly count?: number }) {
  return (
    <div className="@container flex flex-col gap-8" aria-hidden>
      <section className="flex min-w-0 flex-col gap-3">
        <Skeleton className="h-3 w-20 rounded-full" />
        <ul className="grid grid-cols-1 gap-3 @[22rem]:grid-cols-2 @[40rem]:grid-cols-3 @[56rem]:grid-cols-4">
          {Array.from({ length: count }).map((_, index) => (
            <li key={index} className="min-w-0">
              <Skeleton className="h-[7.5rem] w-full rounded-xl" />
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}

/** Memory list rows while CogniMemo fetches. */
export function MorrowMemoryListSkeleton({ count = 5 }: { readonly count?: number }) {
  return (
    <ol className="flex flex-col divide-y divide-border" aria-hidden>
      {Array.from({ length: count }).map((_, index) => (
        <li key={index} className="py-4 first:pt-0">
          <Skeleton className="h-4 w-full rounded-full" />
          <Skeleton className="mt-2 h-4 w-5/6 rounded-full" />
          <div className="mt-2 flex gap-2">
            <Skeleton className="h-5 w-16 rounded-md" />
            <Skeleton className="h-5 w-24 rounded-md" />
          </div>
        </li>
      ))}
    </ol>
  );
}

/** Usage meters and feature rows while entitlements load. */
export function MorrowUsageSkeleton() {
  return (
    <div className="flex flex-col gap-10" aria-hidden>
      <section>
        <Skeleton className="h-4 w-36 rounded-full" />
        <div className="mt-4 flex flex-col divide-y divide-border">
          {Array.from({ length: 4 }).map((_, index) => (
            <div key={index} className="py-4 first:pt-0">
              <div className="flex items-center justify-between gap-4">
                <Skeleton className="h-4 w-36 rounded-full" />
                <Skeleton className="h-4 w-16 rounded-full" />
              </div>
              <Skeleton className="mt-2 h-1.5 w-full rounded-full" />
            </div>
          ))}
        </div>
      </section>
      <section>
        <Skeleton className="h-4 w-32 rounded-full" />
        <div className="mt-4 flex flex-col divide-y divide-border">
          {Array.from({ length: 3 }).map((_, index) => (
            <div key={index} className="flex items-center justify-between gap-4 py-4 first:pt-0">
              <Skeleton className="h-4 w-40 rounded-full" />
              <Skeleton className="h-4 w-20 rounded-full" />
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

/** Sticky plan summary rail on the Usage page. */
export function MorrowUsagePlanRailSkeleton() {
  return (
    <div className="flex flex-col gap-8 lg:sticky lg:top-0" aria-hidden>
      <section>
        <Skeleton className="h-4 w-24 rounded-full" />
        <Skeleton className="mt-3 h-5 w-28 rounded-full" />
        <Skeleton className="mt-2 h-8 w-20 rounded-full" />
        <div className="mt-4 flex flex-col gap-1.5">
          {Array.from({ length: 4 }).map((_, index) => (
            <Skeleton key={index} className="h-4 w-full rounded-full" />
          ))}
        </div>
      </section>
    </div>
  );
}

/** Pricing plan cards and comparison table while the catalog loads. */
export function MorrowPricingSkeleton() {
  return (
    <div className="flex flex-col gap-12" aria-hidden>
      <section>
        <Skeleton className="h-4 w-24 rounded-full" />
        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {Array.from({ length: 4 }).map((_, index) => (
            <Skeleton key={index} className="h-64 w-full rounded-2xl" />
          ))}
        </div>
        <Skeleton className="mt-10 h-4 w-32 rounded-full" />
        <Skeleton className="mt-4 h-48 w-full rounded-lg" />
      </section>
    </div>
  );
}

/** Vault folders strip + file list area. */
export function MorrowVaultBrowseSkeleton() {
  return (
    <div className="space-y-8" aria-hidden>
      <div className="space-y-4">
        <Skeleton className="h-6 w-24 rounded-full" />
        <div className="flex gap-3">
          {Array.from({ length: 4 }).map((_, index) => (
            <Skeleton key={index} className="h-36 w-28 rounded-2xl" />
          ))}
        </div>
      </div>
      <div className="space-y-4">
        <Skeleton className="h-6 w-20 rounded-full" />
        <Skeleton className="h-64 rounded-2xl" />
      </div>
    </div>
  );
}
